import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type { Socket } from "socket.io-client";
import { toast } from "react-toastify";
import {
  LiveViewEvents,
  REQUEST_TIMEOUT_MS,
  VIEWER_LEFT_GRACE_MS,
  type Ack,
  type EventsPayload,
  type SessionStatus,
  type SessionUpdate
} from "shared/utils/live-view/protocol";
import { createReassembler } from "shared/utils/live-view/chunking";
import { useLiveViewTicket } from "./useLiveViewTicket";
import {
  initialSessionState,
  isSessionOpen,
  sessionReducer,
  type LiveViewSessionState
} from "./sessionState";
import { EMPTY_PRESENCE, type PresenceMap } from "./presence";
import { createReplayEventBuffer } from "./eventBuffer";
import { createStreamHealth, type ResyncReason } from "./streamHealth";
import { openLiveViewSocket, subscribePresence } from "./liveViewSocket";

const ACK_TIMEOUT_MS = 10_000;
/** Events that can arrive before the `request` ack (a relay race). */
const MAX_EARLY_PAYLOADS = 200;
/** How long the server gets to say "expired" before the viewer decides. */
const LOCAL_EXPIRY_GRACE_MS = 10_000;
/**
 * A session can't outlive this much disconnection: the applicant's recorder
 * stops this long after its last viewer left.
 */
const CONNECTION_LOST_AFTER_MS = VIEWER_LEFT_GRACE_MS;

const REQUEST_ERRORS: Record<string, string> = {
  applicant_not_present: "The applicant isn't in the application right now.",
  timeout: "Live view didn't respond. Try again in a moment."
};

const requestErrorMessage = (code: string) =>
  REQUEST_ERRORS[code] ?? `Couldn't start live view (${code}).`;

const isEventsPayload = (value: unknown): value is EventsPayload => {
  if (!value || typeof value !== "object") return false;
  const payload = value as Record<string, unknown>;
  return (
    typeof payload.session_id === "string" && typeof payload.data === "string"
  );
};

const isSessionUpdate = (value: unknown): value is SessionUpdate => {
  if (!value || typeof value !== "object") return false;
  const update = value as Record<string, unknown>;
  return (
    typeof update.session_id === "string" && typeof update.status === "string"
  );
};

const noop = () => undefined;

const ackErrorCode = (err: Error | null, ack: Ack | undefined) => {
  if (err) return "timeout";
  return ack && !ack.ok ? ack.error : "unknown";
};

/**
 * Viewer side of BoomView for one application: holds the `/live-view`
 * socket, the presence map (who's in the applicant app now) and one watch
 * session at a time. Replay events land in `events`; the replay stage
 * attaches to it.
 */
export const useLiveView = (applicationId: string | undefined) => {
  const { enabled, getFreshToken } = useLiveViewTicket(applicationId);

  const [session, dispatch] = useReducer(sessionReducer, initialSessionState);
  const [presence, setPresenceState] = useState<PresenceMap>(EMPTY_PRESENCE);
  const [connected, setConnected] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const presenceRef = useRef<PresenceMap>(EMPTY_PRESENCE);
  const sessionRef = useRef<LiveViewSessionState>(session);
  sessionRef.current = session;

  // The session id lives in a ref too: events and acks race React renders.
  const sessionIdRef = useRef<string | null>(null);
  const requestSeqRef = useRef(0);
  const awaitingAckRef = useRef(false);
  // Events that beat the `request` ack, by session id (replayed on the ack).
  const earlyPayloadsRef = useRef(new Map<string, EventsPayload[]>());
  // Set when a session was lost to a long disconnect: stopped on reconnect.
  const lostSessionIdRef = useRef<string | null>(null);
  // Set when a re-join was inferred "paused" because presence lacked the
  // applicant; their presence arriving later means they're there after all.
  const inferredAwayRef = useRef<string | null>(null);
  // Lazy initializers: created once, not on every render.
  const [reassembler] = useState(createReassembler);
  const [events] = useState(createReplayEventBuffer);

  /**
   * The replay drifted from the applicant's screen (see streamHealth): ask for
   * a fresh full snapshot. Re-joining is idempotent on the server, which then
   * asks the applicant for one.
   */
  const requestSnapshot = useCallback((reason: ResyncReason) => {
    const socket = socketRef.current;
    const sessionId = sessionIdRef.current;
    if (!socket?.connected || !sessionId) return false;
    if (sessionRef.current.status !== "live") return false;
    console.warn(`[LiveView] Replay out of sync (${reason}), resyncing`);
    socket.emit(LiveViewEvents.Join, { session_id: sessionId }, noop);
    return true;
  }, []);
  const [health] = useState(() => createStreamHealth({ requestSnapshot }));

  const setPresence = useCallback((next: PresenceMap) => {
    if (next === presenceRef.current) return;
    presenceRef.current = next;
    setPresenceState(next);
  }, []);

  const feed = useCallback(
    (payload: EventsPayload) => {
      try {
        const batch = reassembler.push(payload);
        if (!batch?.length) return;
        // Before replaying: rrweb may warn while applying the batch.
        health.batch(payload.seq, batch);
        events.push(batch);
      } catch {
        console.warn("[LiveView] Dropped an unreadable events batch");
        health.lost();
      }
    },
    [reassembler, events, health]
  );

  const resetStream = useCallback(() => {
    reassembler.reset();
    events.clear();
    health.reset();
    earlyPayloadsRef.current = new Map();
  }, [reassembler, events, health]);

  const sendStop = useCallback((sessionId: string) => {
    socketRef.current?.emit(
      LiveViewEvents.Stop,
      { session_id: sessionId },
      noop
    );
  }, []);

  /** Ends the current session (if any) from the viewer's side. */
  const stop = useCallback(() => {
    // A request still waiting for its ack is stopped when the ack lands.
    requestSeqRef.current += 1;
    awaitingAckRef.current = false;
    const sessionId = sessionIdRef.current;
    sessionIdRef.current = null;
    health.reset();
    if (sessionId && isSessionOpen(sessionRef.current.status))
      sendStop(sessionId);
    dispatch({ type: "stopped" });
  }, [sendStop, health]);

  /** Asks the applicant to share; false when a request can't be sent now. */
  const watch = useCallback(
    (applicantId: number) => {
      const socket = socketRef.current;
      if (isSessionOpen(sessionRef.current.status)) return false;
      if (!socket?.connected) {
        toast.error("Live view isn't connected yet. Try again in a moment.");
        return false;
      }

      resetStream();
      sessionIdRef.current = null;
      awaitingAckRef.current = true;
      requestSeqRef.current += 1;
      const seq = requestSeqRef.current;
      dispatch({ type: "watch", applicantId });

      // Not socket.timeout(): it drops an ack that arrives after the timeout,
      // and a late "ok" still means the server opened a session to stop.
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        if (seq !== requestSeqRef.current) return;
        awaitingAckRef.current = false;
        earlyPayloadsRef.current = new Map();
        dispatch({ type: "requestFailed", error: "timeout" });
        toast.error(requestErrorMessage("timeout"));
      }, ACK_TIMEOUT_MS);

      socket.emit(
        LiveViewEvents.Request,
        { applicant_id: applicantId },
        (ack?: Ack<{ session_id: string }>) => {
          clearTimeout(timer);
          if (timedOut || seq !== requestSeqRef.current) {
            // Timed out, stopped or closed before the ack: end the orphan.
            if (ack?.ok) sendStop(ack.session_id);
            return;
          }
          awaitingAckRef.current = false;
          const early = earlyPayloadsRef.current;
          earlyPayloadsRef.current = new Map();

          if (!ack || !ack.ok) {
            const code = ackErrorCode(null, ack);
            dispatch({ type: "requestFailed", error: code });
            toast.error(requestErrorMessage(code));
            return;
          }

          sessionIdRef.current = ack.session_id;
          dispatch({ type: "requestAcked", sessionId: ack.session_id });
          (early.get(ack.session_id) ?? []).forEach(feed);
        }
      );
      return true;
    },
    [feed, resetStream, sendStop]
  );

  useEffect(() => {
    if (!enabled || !applicationId) return;

    const rejoin = (socket: Socket, sessionId: string) => {
      socket
        .timeout(ACK_TIMEOUT_MS)
        .emit(
          LiveViewEvents.Join,
          { session_id: sessionId },
          (err: Error | null, ack?: Ack<{ status: SessionStatus }>) => {
            if (sessionIdRef.current !== sessionId || err) return;
            if (!ack || !ack.ok) {
              sessionIdRef.current = null;
              dispatch({
                type: "joinFailed",
                sessionId,
                error: ackErrorCode(err, ack)
              });
              return;
            }
            // The server keeps "active" while the applicant is away; presence
            // (the snapshot arrives before this ack) tells us they left.
            const applicantId = sessionRef.current.applicantId;
            const away =
              ack.status === "active" &&
              applicantId !== null &&
              !presenceRef.current.has(applicantId);
            inferredAwayRef.current = away ? sessionId : null;
            dispatch({
              type: "joinAcked",
              sessionId,
              status: away ? "paused" : ack.status
            });
            // The re-join asked the applicant for a fresh snapshot.
            if (!away && ack.status === "active") health.expectSnapshot();
          }
        );
    };

    const { socket, close } = openLiveViewSocket({
      getFreshToken,
      onConnect: () => {
        setConnected(true);
        const lost = lostSessionIdRef.current;
        lostSessionIdRef.current = null;
        if (lost) socket.emit(LiveViewEvents.Stop, { session_id: lost }, noop);
        const sessionId = sessionIdRef.current;
        if (sessionId && isSessionOpen(sessionRef.current.status))
          rejoin(socket, sessionId);
      },
      onDisconnect: ({ dropped }) => {
        setConnected(false);
        if (!dropped) return;
        setPresence(EMPTY_PRESENCE);
        // Half-received batches are gone; the rejoin brings a fresh snapshot.
        reassembler.reset();
        health.reset();
      }
    });
    socketRef.current = socket;

    const stopPruning = subscribePresence(socket, {
      read: () => presenceRef.current,
      write: setPresence,
      // Only this application's applicants (entries always carry the id).
      accepts: (entry) => String(entry.application_id) === applicationId
    });

    socket.on(LiveViewEvents.Session, (update: unknown) => {
      if (!isSessionUpdate(update)) return;
      // The server's word replaces any status inferred from presence.
      if (update.session_id === inferredAwayRef.current)
        inferredAwayRef.current = null;
      dispatch({ type: "sessionUpdate", update });
      // Accepted or resumed: the applicant sends a full snapshot now.
      if (
        update.status === "active" &&
        update.session_id === sessionIdRef.current
      )
        health.expectSnapshot();
    });

    socket.on(LiveViewEvents.Events, (payload: unknown) => {
      if (!isEventsPayload(payload)) return;
      if (awaitingAckRef.current) {
        const early = earlyPayloadsRef.current;
        const pending = early.get(payload.session_id) ?? [];
        early.set(
          payload.session_id,
          [...pending, payload].slice(-MAX_EARLY_PAYLOADS)
        );
        return;
      }
      if (payload.session_id === sessionIdRef.current) feed(payload);
    });

    return () => {
      stopPruning();
      const sessionId = sessionIdRef.current;
      if (sessionId && isSessionOpen(sessionRef.current.status))
        socket.emit(LiveViewEvents.Stop, { session_id: sessionId }, noop);
      sessionIdRef.current = null;
      awaitingAckRef.current = false;
      requestSeqRef.current += 1;
      close();
      socketRef.current = null;
      setConnected(false);
      setPresence(EMPTY_PRESENCE);
      resetStream();
      dispatch({ type: "reset" });
    };
  }, [
    enabled,
    applicationId,
    getFreshToken,
    feed,
    resetStream,
    setPresence,
    reassembler,
    health
  ]);

  // A re-join inferred "paused" (presence lacked the applicant) goes live as
  // soon as their presence shows up: the server already said "active".
  useEffect(() => {
    const sessionId = inferredAwayRef.current;
    if (!sessionId || session.status !== "paused") return;
    if (session.sessionId !== sessionId || session.applicantId === null) return;
    if (!presence.has(session.applicantId)) return;
    inferredAwayRef.current = null;
    dispatch({ type: "joinAcked", sessionId, status: "active" });
  }, [presence, session.status, session.sessionId, session.applicantId]);

  /**
   * While disconnected the stage says "Connection lost, reconnecting…". If
   * the socket is still down when the applicant's recorder would give up on
   * us (VIEWER_LEFT_GRACE_MS), the session is lost: fail it, and stop it on
   * the server once the socket is back.
   */
  const sessionOpen = isSessionOpen(session.status);
  useEffect(() => {
    if (connected || !sessionOpen) return;
    const timer = setTimeout(() => {
      const sessionId = sessionIdRef.current;
      lostSessionIdRef.current = sessionId;
      sessionIdRef.current = null;
      awaitingAckRef.current = false;
      requestSeqRef.current += 1;
      dispatch({ type: "connectionLost" });
    }, CONNECTION_LOST_AFTER_MS);
    return () => clearTimeout(timer);
  }, [connected, sessionOpen]);

  /**
   * The server expires an unanswered request (status "expired"). If it stays
   * silent, expire it here too, and stop it so both sides agree: 10 s after
   * the applicant left, or 10 s past the server's own request timeout.
   */
  const expireLocally = useCallback(
    (sessionId: string) => {
      if (sessionIdRef.current !== sessionId) return;
      sessionIdRef.current = null;
      sendStop(sessionId);
      dispatch({ type: "expiredLocally", sessionId });
    },
    [sendStop]
  );

  const waitingSessionId =
    session.status === "waiting" ? session.sessionId : null;
  const applicantPresent =
    session.applicantId !== null && presence.has(session.applicantId);

  useEffect(() => {
    if (!waitingSessionId) return;
    const timer = setTimeout(
      () => expireLocally(waitingSessionId),
      REQUEST_TIMEOUT_MS + LOCAL_EXPIRY_GRACE_MS
    );
    return () => clearTimeout(timer);
  }, [waitingSessionId, expireLocally]);

  useEffect(() => {
    // Only while connected: a disconnect empties presence too.
    if (!waitingSessionId || applicantPresent || !connected) return;
    const timer = setTimeout(
      () => expireLocally(waitingSessionId),
      LOCAL_EXPIRY_GRACE_MS
    );
    return () => clearTimeout(timer);
  }, [waitingSessionId, applicantPresent, connected, expireLocally]);

  // Leaving the page ends the session right away instead of leaving the
  // applicant recording until the server notices the viewer is gone. One
  // listener for the hook's lifetime; it reads the status through the ref.
  useEffect(() => {
    const onPageHide = () => {
      if (isSessionOpen(sessionRef.current.status)) stop();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [stop]);

  return {
    enabled,
    connected,
    presence,
    session,
    events,
    /** For the replay stage: rrweb warned that the replay drifted. */
    reportReplayWarning: health.replayWarning,
    watch,
    stop
  };
};

export type LiveViewController = ReturnType<typeof useLiveView>;
